import React from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Target, Star, ArrowRight } from 'lucide-react';
import { Member } from '@/types/member';
import { useNavigate } from 'react-router-dom';

interface ProfileCompletionBannerProps {
  member: Member;
}

// Fonction locale pour calculer la complétude du profil
const calculateProfileCompleteness = (member: Member): number => {
  if (!member) return 0;
  
  const fields = [
    member.name, member.email, member.company, member.role, 
    member.industry, member.city, member.bio, member.phone
  ];
  const arrayFields = [member.skills, member.needs, member.tags, member.languages];
  
  let score = 0;
  fields.forEach(field => field && field.trim() ? score += 10 : null);
  arrayFields.forEach(arr => arr && arr.length > 0 ? score += 10 : null);
  
  return Math.min(100, score);
};

const ProfileCompletionBanner = ({ member }: ProfileCompletionBannerProps) => {
  const navigate = useNavigate();
  
  // Handle null member case
  if (!member) {
    return null;
  }
  
  const completeness = calculateProfileCompleteness(member);
  
  const getMissingFields = () => {
    if (!member) return [];
    
    const missing = [];
    if (!member.skills || member.skills.length === 0) missing.push('compétences');
    if (!member.needs || member.needs.length === 0) missing.push('besoins');
    if (!member.bio || member.bio.trim() === '') missing.push('biographie');
    if (!member.industry || member.industry.trim() === '') missing.push('secteur');
    if (!member.tags || member.tags.length === 0) missing.push('centres d\'intérêt');
    return missing;
  };

  const missingFields = getMissingFields();
  
  if (completeness >= 100) {
    return null;
  }

  if (completeness >= 80) {
    return (
      <Alert className="border-green-500 bg-green-50 dark:bg-green-950/20">
        <Star className="h-4 w-4 text-green-600 dark:text-green-400" />
        <AlertDescription className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-medium text-green-700 dark:text-green-300">
              🎉 Profil excellent ! Suggestions premium activées
            </span>
            <Badge variant="secondary" className="bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300">
              {completeness}% complété
            </Badge>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  if (completeness >= 60) {
    return (
      <Alert className="border-blue-500 bg-blue-50 dark:bg-blue-950/20">
        <Target className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        <AlertDescription className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-medium text-blue-700 dark:text-blue-300">
              ✓ Suggestions activées - Complétez pour des recommendations premium
            </span>
            <Badge variant="secondary" className="bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
              {completeness}% complété
            </Badge>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  if (completeness >= 40) {
    return (
      <Alert className="border-orange-500 bg-orange-50 dark:bg-orange-950/20">
        <Target className="h-4 w-4 text-orange-600 dark:text-orange-400" />
        <AlertDescription>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-medium text-orange-700 dark:text-orange-300">
                ⚠️ Encore {60 - completeness}% pour débloquer les suggestions
              </span>
              <Badge variant="outline" className="border-orange-500 text-orange-700 dark:text-orange-300">
                {completeness}% complété
              </Badge>
            </div>
            
            <Progress value={completeness} className="h-2" />
            
            <div className="flex items-center justify-between">
              <div className="text-sm text-orange-600 dark:text-orange-400">
                Champs manquants : {missingFields.slice(0, 3).join(', ')}
                {missingFields.length > 3 && ` et ${missingFields.length - 3} autres`}
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/profile?edit=true')}
                className="h-8 border-orange-500 text-orange-700 hover:bg-orange-100 dark:text-orange-300"
              >
                Compléter
                <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert className="border-red-500 bg-red-50 dark:bg-red-950/20">
      <Target className="h-4 w-4 text-red-600 dark:text-red-400" />
      <AlertDescription>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="font-medium text-red-700 dark:text-red-300">
              🚨 Profil incomplet - Suggestions désactivées
            </span>
            <Badge variant="destructive">
              {completeness}% complété
            </Badge>
          </div>
          
          <Progress value={completeness} className="h-2" />
          
          <div className="flex items-center justify-between">
            <div className="text-sm text-red-600 dark:text-red-400">
              Champs manquants : {missingFields.slice(0, 3).join(', ')}
              {missingFields.length > 3 && ` et ${missingFields.length - 3} autres`}
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/profile?edit=true')}
              className="h-8 border-red-500 text-red-700 hover:bg-red-100 dark:text-red-300"
            >
              Compléter
              <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </div>
        </div>
      </AlertDescription>
    </Alert>
  );
};

export default ProfileCompletionBanner;